/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { isDialogFocused, isTargetInteractiveInput } from '@opengeoweb/shared';

export type TimeStepDirection = 'up' | 'down';

const isMac = (): boolean => window.navigator.userAgent.includes(' Mac ');

const hasShortcutModifier = (
  event: KeyboardEvent | React.KeyboardEvent,
): boolean => (isMac() ? event.ctrlKey && event.shiftKey : event.ctrlKey);

export const getTimeStepShortcutDirection = (
  event: KeyboardEvent | React.KeyboardEvent,
): TimeStepDirection | null => {
  if (!hasShortcutModifier(event)) {
    return null;
  }
  if (event.key === 'ArrowUp') {
    return 'up';
  }
  if (event.key === 'ArrowDown') {
    return 'down';
  }
  return null;
};

const focusAdjacentMenuItem = (
  target: Element | null,
  direction: TimeStepDirection,
): void => {
  if (
    !(target instanceof HTMLElement) ||
    target.getAttribute('role') !== 'menuitem'
  ) {
    return;
  }
  const menuItems = Array.from(
    target.parentElement?.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])',
    ) ?? [],
  );
  const currentIndex = menuItems.indexOf(target);
  const nextIndex = currentIndex + (direction === 'up' ? -1 : 1);
  menuItems[nextIndex]?.focus();
};

export interface TimeStepShortcutContextValue {
  pendingDirection: TimeStepDirection | null;
  closeOptionsMenu: () => void;
  consume: () => void;
}

export const TimeStepShortcutContext =
  React.createContext<TimeStepShortcutContextValue | null>(null);

// Listens for the time-step keyboard shortcut on document (capture phase, so it
// runs before any element-level handlers that may stopPropagation). Ignores
// presses while focus is in a text input.
export const useTimeStepShortcutListener = (
  enabled: boolean,
  onShortcut: (direction: TimeStepDirection) => void,
): void => {
  const onShortcutRef = React.useRef(onShortcut);
  React.useEffect(() => {
    onShortcutRef.current = onShortcut;
  });

  React.useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as Element | null;
      if (isTargetInteractiveInput(target) || isDialogFocused()) {
        return;
      }
      const direction = getTimeStepShortcutDirection(event);
      if (!direction) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onShortcutRef.current(direction);
      focusAdjacentMenuItem(target, direction);
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [enabled]);
};
